import React from 'react';
import { Navigate, NavLink, Outlet, useLocation, useParams } from 'react-router-dom';
import { Box, Button, Card, CardContent, Grid, Skeleton, Stack, Tab, Tabs, Typography } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { useShell } from '../components/Layout';
import { canTab, isMember } from '../utils/access';
import MarketplaceIcon from '../components/common/MarketplaceIcon';
import { MP_SECTIONS, hasSync, isKnownMarketplace, mpPath } from '../utils/marketplaces';
import { pluralize } from '../utils/format';
import { marketplaceMeta, tokens } from '../theme';

const BENEFITS = [
  'История продаж, заказов и расходов с начала 2026 года — без ручных выгрузок',
  'Выручка, комиссия, логистика, штрафы и чистая прибыль по каждому товару',
  'Себестоимость, налоги и остатки в одном месте',
];

const ConnectHero = ({ marketplace, onConnect }) => {
  const meta = marketplaceMeta(marketplace);
  return (
    <Card sx={{ background: tokens.heroGradient, border: 'none', position: 'relative', overflow: 'hidden' }}>
      <Box aria-hidden sx={{ position: 'absolute', right: -90, top: -110, width: 380, height: 380, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.35)' }} />
      <CardContent sx={{ p: { xs: 3, md: 5 }, position: 'relative' }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography variant="h2" sx={{ mb: 1 }}>Подключите {meta.label}</Typography>
            <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 560 }}>
              Нужен только API-ключ из личного кабинета продавца. MARQIT проверит доступ и дальше будет обновлять данные сам.
            </Typography>
            <Stack spacing={1.25} sx={{ mb: 3.5 }}>
              {BENEFITS.map((b) => (
                <Stack key={b} direction="row" spacing={1.25} alignItems="center">
                  <CheckCircleRoundedIcon sx={{ color: tokens.primary, fontSize: 20 }} />
                  <Typography>{b}</Typography>
                </Stack>
              ))}
            </Stack>
            <Button variant="contained" size="large" startIcon={<AddRoundedIcon />} onClick={onConnect}>Подключить {meta.label}</Button>
            {!hasSync(marketplace) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                Загрузка данных {meta.label} появится в ближайшем обновлении; подключить кабинет можно уже сейчас.
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
            <Box sx={{ position: 'relative', width: 220, height: 220, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MarketplaceIcon marketplace={marketplace} size={112} />
              <Box sx={{ position: 'absolute', right: 20, bottom: 26, width: 52, height: 52, borderRadius: '50%', bgcolor: tokens.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: tokens.shadowRaised }}>
                <AddRoundedIcon sx={{ fontSize: 30 }} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Frame of every marketplace screen: header, section tabs, and the section itself. Sections get
// { marketplace, accounts } through the outlet context.
const MarketplacePage = () => {
  const { marketplace } = useParams();
  const location = useLocation();
  const { accounts, loaded, openConnect, profile } = useShell();
  if (!isKnownMarketplace(marketplace)) return <Navigate to="/dashboard" replace />;

  const meta = marketplaceMeta(marketplace);
  const mpAccounts = accounts.filter((a) => a.marketplace === marketplace);
  const connected = mpAccounts.length > 0;
  const rest = location.pathname.slice(mpPath(marketplace).length).replace(/^\//, '').split('/')[0];
  const section = MP_SECTIONS.find((s) => s.key === rest) ? rest : '';
  const tabs = MP_SECTIONS.filter((s) => canTab(profile, s.key));
  const member = isMember(profile);

  return (
    <Box sx={{ maxWidth: 1480, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ mb: 2.5 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
          <MarketplaceIcon marketplace={marketplace} size={56} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" sx={{ color: tokens.primaryDark, display: 'block', lineHeight: 1.4 }}>Маркетплейс</Typography>
            <Typography variant="h1" component="h1" sx={{ fontSize: { xs: '1.6rem', md: '2rem' } }} noWrap>{meta.label}</Typography>
          </Box>
        </Stack>
        {connected && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {mpAccounts.length} {pluralize(mpAccounts.length, 'кабинет', 'кабинета', 'кабинетов')}
            </Typography>
            {!member && <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={() => openConnect(marketplace)}>Добавить кабинет</Button>}
          </Stack>
        )}
      </Stack>

      {!loaded ? (
        <Skeleton variant="rounded" height={320} sx={{ borderRadius: '16px' }} />
      ) : !connected && member ? (
        <Card><CardContent sx={{ py: 5, textAlign: 'center' }}>
          <Typography variant="h3" sx={{ mb: 1 }}>Кабинеты {meta.label} вам не открыты</Typography>
          <Typography color="text.secondary">Доступ к кабинетам выдаёт владелец аккаунта в разделе «Команда».</Typography>
        </CardContent></Card>
      ) : !connected ? (
        <ConnectHero marketplace={marketplace} onConnect={() => openConnect(marketplace)} />
      ) : !canTab(profile, section) ? (
        tabs.length ? <Navigate to={mpPath(marketplace, tabs[0].key)} replace /> : (
          <Card><CardContent sx={{ py: 5, textAlign: 'center' }}>
            <Typography variant="h3" sx={{ mb: 1 }}>Разделы площадки вам не открыты</Typography>
            <Typography color="text.secondary">Обратитесь к владельцу аккаунта: он настраивает доступы в разделе «Команда».</Typography>
          </CardContent></Card>
        )
      ) : (
        <>
          <Box sx={{ mb: 3, borderBottom: `1px solid ${tokens.border}` }}>
            <Tabs value={section} variant="scrollable" allowScrollButtonsMobile aria-label={`Разделы ${meta.label}`}>
              {tabs.map((s) => (
                <Tab key={s.key || 'overview'} value={s.key} label={s.label} component={NavLink} to={mpPath(marketplace, s.key)} />
              ))}
            </Tabs>
          </Box>
          <Outlet context={{ marketplace, accounts: mpAccounts }} />
        </>
      )}
    </Box>
  );
};

export default MarketplacePage;
